import { Box, Button, CircularProgress, LinearProgress, Typography, useTheme } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { CAMPAIGN_TYPES, EVENT_TYPES } from '@/api/events/log'
import { useCheckAnswer } from '@/api/quizzes/check-answer'
import { useQuizById } from '@/api/quizzes/quiz-by-id'
import Summary from '@/api/quizzes/Summary'
import { useVideosTraining } from '@/api/videos/get-training'
import Logo from '@/assets/images/logo-clean-blue.svg'
import LoadingContainer from '@/common/components/LoadingContainer/LoadingContainer'
import useDeviceDetection, { DeviceType } from '@/common/hooks/useDeviceDetection'
import useLogEvent from '@/common/hooks/useLogEvent'
import useToast from '@/common/hooks/useToast'
import { useUrlSearchQuery } from '@/common/hooks/useUrlQuery'
import { isValidLanguageKey } from '@/common/utils/language-utils'
import InvalidToken from '../TrainingCampaignView/InvalidToken'
import Answer from './Answer'
import { LanguageSelect } from './LanguageSelect'
import { languages } from '@/assets/locales/languages'

import CywarenessLogo from '@/assets/images/logo-be-awere-stay-safe.svg'

const Quiz: FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const navigateTo = useNavigate()
  const { t } = useTranslation()
  const { campaignToken, quizId } = useParams()
  const queryParams = useUrlSearchQuery()
  const { log } = useLogEvent()
  const { errorToast } = useToast()

  const [selectedLanguage, setSelectedLanguage] = useState('en')
  const isPreview = campaignToken === 'preview'
  const isRtl = selectedLanguage === 'ar' || selectedLanguage === 'he'
  const deviceType = useDeviceDetection()

  const { data: videosTrainingData, isLoading: isPending } = useVideosTraining(
    campaignToken !== 'preview' ? campaignToken : undefined
  )

  const { data: quizByIdData, isLoading } = useQuizById(
    {
      quiz_id: quizId as string,
      campaign: campaignToken as string,
    },
    { enabled: isPreview }
  )

  const quizData = useMemo(
    () =>
      isPreview // When preview mode use the old quiz-by-id endpoint
        ? quizByIdData
        : videosTrainingData?.videos?.find((v) => {
            return v.quiz_id === quizId
          }),
    [videosTrainingData, quizByIdData, isPreview, quizId]
  )

  const campaignData = useMemo(
    () => videosTrainingData?.videos.find((v) => v.quiz_id === quizId),
    [videosTrainingData, quizId]
  )

  const [checkAnswer, { isLoading: isCheckingAnswer }] = useCheckAnswer()
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const totalQuestions = quizData?.quiz_questions_with_answers?.length || 1
  const isFinalQuestion = currentQuestionIndex + 1 === totalQuestions
  const [showSummary, setShowSummary] = useState(false)
  const [correctQuestions, setCorrectQuestions] = useState(0)
  const [hasAnsweredQuestion, setHasAnsweredQuestion] = useState(false)
  const [checkedAnswers, setCheckedAnswers] = useState<{
    [id: string]: { isCorrect: boolean }
  }>({})

  const isPackage = useMemo(() => {
    return (videosTrainingData?.videos?.length ?? 0) > 1
  }, [videosTrainingData])

  useEffect(() => {
    log({
      campaignToken,
      campaignType: CAMPAIGN_TYPES.TRAINING,
      eventType: EVENT_TYPES.QUIZ_STARTED,
      additionalData: {
        asset_id: campaignData?.id,
        quiz_id: quizData?._id,
      },
    })
  }, [])

  function handleExit() {
    navigateTo(
      `/content/completed-training/${campaignToken}${queryParams.language ? `?language=${queryParams.language}` : ''}${
        isPackage ? `&index=${quizData && '_id' in quizData ? quizData._id : quizData?.id}` : ''
      }`
    )
  }

  async function handleCheckAnswer(answerId: string) {
    if (quizData) {
      try {
        const result = await checkAnswer({
          answerId,
          campaignToken,
          questionId: quizData.quiz_questions_with_answers[currentQuestionIndex]?.id,
        })
        log({
          campaignToken: campaignToken,
          eventType: EVENT_TYPES.QUIZ_ANSWER,
          campaignType: CAMPAIGN_TYPES.TRAINING,
          additionalData: {
            asset_id: campaignData?.id,
            quiz_id: quizData?._id,
            question: result?.question,
            answer: result?.answer,
            isCorrect: result?.is_correct,
          },
        })
        if (result) {
          if (result.is_correct) {
            setCorrectQuestions(correctQuestions + 1)
            setCheckedAnswers((prevState) => ({
              ...prevState,
              [answerId]: {
                isCorrect: true,
              },
            }))
          } else {
            setCheckedAnswers((prevState) => ({
              ...prevState,
              [answerId]: {
                isCorrect: false,
              },
              [result.correct_answers[0]]: { isCorrect: true },
            }))
          }

          setHasAnsweredQuestion(true)
        }
      } catch (e) {
        if (isPreview) {
          errorToast(t('quizPage.errors.previewCampaign'))
        } else {
          errorToast(t('quizPage.errors.generic'))
        }
      }
    }
  }

  function isCorrectAnswer(answerId: string): boolean | undefined {
    return checkedAnswers[answerId] ? checkedAnswers[answerId].isCorrect : undefined
  }

  function handleNext() {
    setCurrentQuestionIndex(currentQuestionIndex + 1)
    setHasAnsweredQuestion(false)
  }

  function handleFinish() {
    setShowSummary(true)
    if (!isPreview) {
      log({
        campaignToken,
        campaignType: CAMPAIGN_TYPES.TRAINING,
        eventType: EVENT_TYPES.QUIZ_COMPLETED,
        additionalData: {
          asset_id: campaignData?.id,
          quiz_id: quizData && '_id' in quizData ? quizData._id : quizData?.id,
        },
      })
    }
  }

  function handleChangeLanguage(language: string) {
    setSelectedLanguage(language)
    log({
      campaignToken,
      campaignType: CAMPAIGN_TYPES.TRAINING,
      eventType: EVENT_TYPES.QUIZ_LANGUAGE_CHANGE,
      additionalData: {
        asset_id: campaignData?.id,
        quiz_id: quizData?._id,
        language: language,
      },
    })
  }

  const quizLanguages = useMemo(() => {
    if (!quizData?.quiz_questions_with_answers) return []

    return quizData.quiz_questions_with_answers.reduce<string[]>((acc, question) => {
      if ('question_translations' in question) {
        const languages = Object.keys(question.question_translations)
        return [...new Set([...acc, ...languages])]
      }
      return acc
    }, [])
  }, [quizData])

  useEffect(() => {
    const isValidLanguageParam = isValidLanguageKey(queryParams.language)
    if (quizLanguages) {
      if (isValidLanguageParam && quizLanguages.includes(queryParams.language)) {
        setSelectedLanguage(queryParams.language)
      } else if (quizLanguages.length && !quizLanguages.includes('en')) {
        setSelectedLanguage(quizLanguages[0])
      }
    }
  }, [queryParams.language, quizLanguages])

  return (
    <Box display="flex" flexDirection="column" alignItems="center" minHeight="100vh">
      <img
        src={CywarenessLogo}
        style={{
          margin: theme.spacing(4),
        }}
      />
      <Box sx={{ backgroundColor: theme.palette.cyan[500], width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Typography fontSize={18} fontWeight={theme.typography.fontWeightSemiBold} padding={1}>
          {showSummary ? 'Quiz Results' : 'Quiz'}
        </Typography>
      </Box>

      {!isPending || !isLoading ? (
        !quizData ? (
          <InvalidToken />
        ) : (
          <Box display="flex" flexDirection="column" width="100%" flex={1}>
            <Box padding={4} flex={1}>
              <Box
                display="flex"
                flexDirection={deviceType === DeviceType.Mobile ? 'row-reverse' : 'column'}
                justifyContent={'space-between'}>
                <Box display="flex" justifyContent="flex-end">
                  <LanguageSelect
                    options={
                      quizLanguages?.map((language) => ({
                        label: languages[language as keyof typeof languages].name,
                        value: language,
                      })) || []
                    }
                    value={selectedLanguage}
                    handleChange={handleChangeLanguage}
                  />
                </Box>
                <Typography
                  id={'subject'}
                  paddingTop={deviceType === DeviceType.Mobile ? 2 : 0}
                  paddingBottom={4}
                  textAlign={deviceType === DeviceType.Mobile ? 'start' : 'center'}
                  fontSize={deviceType === DeviceType.Mobile ? 16 : 24}
                  fontWeight={theme.typography.fontWeightMedium}>
                  {campaignData?.title}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  flexGrow: 1,
                }}>
                {showSummary ? (
                  <Summary totalQuestions={totalQuestions} correctAnswers={correctQuestions} />
                ) : (
                  <>
                    <Typography
                      id={'subject'}
                      paddingBottom={2}
                      fontWeight={theme.typography.fontWeightMedium}
                      style={{
                        textAlign: isRtl ? 'right' : 'left',
                        direction: isRtl ? 'rtl' : 'ltr',
                      }}>
                      {
                        quizData.quiz_questions_with_answers[currentQuestionIndex]?.question_translations[
                          selectedLanguage
                        ]
                      }
                    </Typography>
                    {quizData.quiz_questions_with_answers[currentQuestionIndex].answers.map((answer, index) => (
                      <Answer
                        answer={answer.answer_translations[selectedLanguage]}
                        questionIndex={index}
                        isCorrect={isCorrectAnswer(answer.id)}
                        handleClick={() => handleCheckAnswer(answer.id)}
                        disabled={hasAnsweredQuestion}
                      />
                    ))}
                  </>
                )}
              </Box>
            </Box>
            <Box className={classes.footer}>
              {showSummary ? (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Button
                    disabled={!hasAnsweredQuestion}
                    style={{ width: 200 }}
                    onClick={handleExit}
                    variant={'contained'}>
                    Done!!
                  </Button>
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <div className={classes.progressContainer}>
                    <div className={classes.progressBar}>
                      <LinearProgress
                        value={((currentQuestionIndex + 1) / totalQuestions) * 100}
                        variant={'determinate'}
                        color={'secondary'}
                      />
                    </div>
                    <Typography noWrap>
                      {currentQuestionIndex + 1} / {totalQuestions}
                    </Typography>
                  </div>
                  <Button
                    disabled={!hasAnsweredQuestion && !isPreview}
                    onClick={isFinalQuestion ? handleFinish : handleNext}
                    variant={'contained'}
                    sx={{ marginRight: 1, width: 144 }}>
                    {isCheckingAnswer ? (
                      <CircularProgress color={'inherit'} size={24} />
                    ) : (
                      t(isFinalQuestion ? 'Check Results' : 'Next')
                    )}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )
      ) : (
        <LoadingContainer />
      )}
      <img className={classes.logoWatermark} src={Logo} alt="logo" />
    </Box>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    closeButton: {
      marginLeft: theme.spacing(2),
    },

    flexCenter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      flexGrow: 1,
    },

    languageContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      right: 0,
    },
    innerTitleContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    logo: {
      height: 40,
      position: 'absolute',
      left: 0,
    },
    textLogo: {
      height: 20,
      marginRight: theme.spacing(2),
      position: 'absolute',
      right: 0,
      float: 'right',
    },
    footer: {
      padding: theme.spacing(2, 0),
      background: theme.palette.grey.A100,
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      marginRight: theme.spacing(6),
    },
    progressBar: {
      width: 240,
      padding: theme.spacing(2),
    },
    answersContainer: {
      marginTop: theme.spacing(2),

      maxWidth: 600,
    },
    logoWatermark: {
      position: 'fixed',
      bottom: -100,
      left: -50,
      opacity: 0.4,
      zIndex: -1,
      height: 400,
      transform: 'rotate(20deg)',
      pointerEvents: 'none',
    },
  })
)

export default Quiz
